/**
 *
 * General
 *
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useDialog from 'containers/Dialog/useDialog.js';
import Form4 from 'components/interaction/Form4/index.jsx';
import useVideoEnabled from 'components/VideoConf/useVideoEnabled.js';
import { intlBold } from 'utils/intlFunctions.jsx';
import useSaveMeetingSet from '../../hooks/useSaveMeetingSet.js';
import messages from './messages.js';
import schema from './form.js';

const Wrapper = styled.div`
  & > .form-group > .flex-grow-1 {
    width: 21em;
  }
`;

function General(props) {
  const { onNext, onClose, meetingSet, group, meetingList } = props;
  const defaultValues = { duration: 10, inBetween: 5, groups: [group.groupId], ...meetingSet };
  defaultValues.description = defaultValues.description || '';
  const [values, setValues] = useState(defaultValues);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const saveMeetingSet = useSaveMeetingSet();
  const [videoEnabled] = useVideoEnabled();
  const warnings = useRef(new Set());
  const { dlgAlert } = useDialog();

  async function handleChange(data) {
    const response = await saveMeetingSet(data);
    if (data.id) {
      onNext();
    } else {
      navigate(`${pathname}/${response.id}#step=2`);
    }
  }

  function warnDuration() {
    const hasBlocks = meetingSet.blocks && meetingSet.blocks.list.length > 0;
    if (!warnings.current.has('duration') && hasBlocks) {
      dlgAlert({ message: messages.warnDuration });
      warnings.current.add('duration');
    }
  }

  function warnLocation() {
    const hasAssigned = meetingList.some((meeting) => meeting.meetingLocationType);
    if (!warnings.current.has('location') && hasAssigned) {
      dlgAlert({ message: messages.warnLocation });
      warnings.current.add('location');
    }
  }

  function checkWarning(newValues) {
    const changed = Object.keys(newValues).filter((key) => newValues[key] !== values[key]);
    if (changed.includes('duration') || changed.includes('inBetween')) {
      warnDuration();
    }
    if (changed.includes('meetingLocationType')) {
      warnLocation();
    }
    setValues(newValues);
  }

  const adaptedSchema = videoEnabled ? schema : { ...schema, properties: schema.properties.filter((o) => o.id !== 'meetingLocationType') };

  const videoAlert = (
    <div className="alert alert-info" style={{ marginLeft: '8rem' }}>
      <FormattedMessage {...messages.videoExplain1} values={intlBold} />
      <br />
      <FormattedMessage {...messages.videoExplain2} />
    </div>
  );

  return (
    <>
      <p className="intro-text"><FormattedMessage {...messages.intro} /></p>
      <Form4
        horizontal
        defaultValues={defaultValues}
        schema={adaptedSchema}
        onCancel={onClose}
        onSubmit={handleChange}
        submitText={messages.next}
        onChange={checkWarning}
        // immediateSubmit={Boolean(meetingSet && meetingSet.id)}
      >
        {({ render, action }) => (
          <>
            {render('title')}
            {render('description')}
            {render('duration')}
            {render('inBetween')}
            <div className="d-flex align-items-start mb-3">
              <Wrapper>
                {render('meetingLocationType')}
              </Wrapper>
              <div className="ms-3 mt-2">
                <FormattedMessage {...messages.locationTypeHelp} />
              </div>
            </div>
            {([2, 3].includes(Number(values.meetingLocationType)) && videoAlert)}
            { action }
          </>
        )}
      </Form4>
    </>
  );
}

General.propTypes = {
  group: PropTypes.object,
  meetingSet: PropTypes.object,
  meetingList: PropTypes.array,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
};

export default General;
